/* User profile: identity, taxonomy, settings, logs, access, memory% */
import React from 'react';
import { Icon, Avatar, TaxonomyField, TaxonomyMultiSelect } from './primitives.jsx';
import { useAutoSave, SaveStatus } from '../lib/use-auto-save.jsx';
import { StaffingSection } from './user-profile-modal.jsx';
import { getMyProfile, patchMyProfile, uploadMyPhoto, generateMyIntro, listTaxonomies, getMyTaxonomies, putMyTaxonomies, putAdminUserTaxonomies, myActivity, getMyUsage, getUserUsage, getMyCompliance, getUserCompliance, listProjects as listAllProjects, fetchUserInsights, setUserInsightsOptOut, getMyProfileAdvisor, setMyProfileAdvisor, getUserProfileAdvisor, setUserProfileAdvisor } from '../api.js';

/* ── Editable overview ──────────────────────────────────────────── */

function sameIds(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  for (let i = 0; i < sa.length; i++) if (sa[i] !== sb[i]) return false;
  return true;
}

// The per-user insights doc built by the user-insights daily master.
// `self` (default) reads/toggles the signed-in user's own doc via the
// /api/user-insights self path; an admin viewing someone else passes
// self={false} + that person's userId, which routes through the same
// API's admin path. Copy switches to the third person for the latter.
export const eOSInsightsCard = ({ userId, self = true }) => {
  const [insights, setInsights] = React.useState(undefined);  // undefined = loading
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    fetchUserInsights(self ? undefined : userId).then(setInsights).catch(() => setInsights(null));
  }, [self, userId]);

  if (insights === undefined) return null;  // loading — render nothing to avoid flash
  const optedOut = !!insights?.optOut;
  const stats = insights?.stats || null;
  const updated = insights?.updatedAt ? new Date(insights.updatedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' }) : null;

  const toggle = () => {
    setBusy(true);
    setUserInsightsOptOut(!optedOut, self ? undefined : userId)
      .then(setInsights)
      .catch(() => {})
      .finally(() => setBusy(false));
  };

  return (
    <div style={{background:'var(--bg)',padding:'4px 0',marginBottom:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:8,marginBottom:10}}>
        <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--fg-muted)',fontWeight:600}}>What <span style={{textTransform:'none'}}>eOS</span> knows about {self ? 'you' : 'this person'}</div>
        {!optedOut && updated && <div style={{fontSize:11,color:'var(--fg-muted)'}}>updated {updated}</div>}
      </div>
      {optedOut ? (
        <div style={{fontSize:13,color:'var(--fg-muted)',fontStyle:'italic'}}>
          {self
            ? "Your profile is turned off. eOS isn't collecting or storing anything about you, and your chats aren't personalized from past activity."
            : "This person's profile is turned off. eOS isn't collecting or storing anything about them, and their chats aren't personalized from past activity."}
        </div>
      ) : insights?.body ? (
        <div style={{fontSize:14,lineHeight:1.55,whiteSpace:'pre-wrap'}}>{insights.body}</div>
      ) : (
        <div style={{fontSize:13,color:'var(--fg-muted)',fontStyle:'italic'}}>
          {self
            ? 'eOS is still learning about you — this fills in automatically after a day of activity (chats, searches, staffing actions).'
            : 'eOS is still learning about this person — this fills in automatically after a day of activity (chats, searches, staffing actions).'}
        </div>
      )}
      {!optedOut && stats && (stats.services?.length || stats.industries?.length || stats.clients?.length) ? (
        <div style={{marginTop:12,display:'flex',flexWrap:'wrap',gap:6}}>
          {[...(stats.services || []).slice(0,4), ...(stats.industries || []).slice(0,3)].map((t,i) => (
            <span key={i} style={{fontSize:11.5,padding:'2px 9px',borderRadius:12,background:'var(--bg-alt)',color:'var(--fg-muted)'}}>{t}</span>
          ))}
        </div>
      ) : null}
      <div style={{marginTop:14,paddingTop:12,borderTop:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center',gap:12}}>
        <div style={{fontSize:11.5,color:'var(--fg-muted)'}}>
          {self
            ? <>Visible only to you and firm admins. {optedOut ? 'Turn on to let eOS build a profile that personalizes your chats.' : 'It personalizes your chats.'}</>
            : <>Visible only to this person and firm admins. {optedOut ? 'Turn on to let eOS build a profile that personalizes their chats.' : 'It personalizes their chats.'}</>}
        </div>
        <button onClick={toggle} disabled={busy}
          style={{flexShrink:0,padding:'5px 12px',fontSize:12,fontWeight:600,borderRadius:8,cursor:busy?'default':'pointer',
                  border:'1px solid var(--border)',background:optedOut?'var(--orange)':'var(--bg-alt)',color:optedOut?'#fff':'var(--fg)'}}>
          {busy ? '…' : self ? (optedOut ? 'Build my profile' : "Don't build my profile") : (optedOut ? 'Turn on' : 'Turn off')}
        </button>
      </div>
    </div>
  );
};

const ProfileOverview = ({ user, onSaved }) => {
  const [name, setName] = React.useState(user?.name || '');
  const [office, setOffice] = React.useState(user?.office || '');
  const [offices, setOffices] = React.useState([]);
  const [bio, setBio] = React.useState(user?.bio || '');
  const [linkedin, setLinkedin] = React.useState(user?.linkedinUrl || '');
  const [photoUrl, setPhotoUrl] = React.useState(user?.photoUrl || '');
  // Organizations (replaces the legacy free-text Company input). Loaded
  // from the firm catalog + current memberships; saved via the partial
  // PUT /api/me/taxonomies so the Experience tab's other picks aren't
  // touched. companyIds === null means "not loaded yet".
  const [orgCatalog, setOrgCatalog] = React.useState([]);
  const [companyIds, setCompanyIds] = React.useState(null);
  const [originalCompanyIds, setOriginalCompanyIds] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [introOpen, setIntroOpen] = React.useState(false);

  React.useEffect(() => {
    Promise.all([listTaxonomies(), getMyTaxonomies()])
      .then(([cat, mine]) => {
        setOrgCatalog(cat.companies || []);
        setOffices(cat.offices || []);
        const ids = mine.companyIds || [];
        setCompanyIds(ids);
        setOriginalCompanyIds(ids);
      })
      .catch(() => { setCompanyIds([]); setOriginalCompanyIds([]); });
  }, []);

  // Reset only when a *different* user is shown. We deliberately don't key on
  // updatedAt: auto-save reloads the parent after every keystroke-batch, and
  // re-pulling values then would clobber edits made during the save round-trip.
  React.useEffect(() => {
    setName(user?.name || '');
    setOffice(user?.office || '');
    setBio(user?.bio || '');
    setLinkedin(user?.linkedinUrl || '');
    setPhotoUrl(user?.photoUrl || '');
  }, [user?.id]);

  const orgIdsDirty = companyIds !== null && !sameIds(companyIds, originalCompanyIds);
  const dirty =
    name !== (user?.name || '') ||
    office !== (user?.office || '') ||
    bio !== (user?.bio || '') ||
    linkedin !== (user?.linkedinUrl || '') ||
    photoUrl !== (user?.photoUrl || '') ||
    orgIdsDirty;

  const joined = user?.createdAt
    ? new Date(user.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })
    : '—';

  const save = async () => {
    setBusy(true); setError(null);
    try {
      const tasks = [
        patchMyProfile({
          name: name.trim(),
          office: office.trim() || null,
          bio: bio.trim() || null,
          linkedinUrl: linkedin.trim() || null,
          photoUrl: photoUrl.trim() || null,
        }),
      ];
      if (orgIdsDirty) {
        // Partial PUT — only sends companyIds, so the Experience tab's
        // services/industries/clients/projects are left untouched.
        tasks.push(putMyTaxonomies({ companyIds }).then(next => {
          setOriginalCompanyIds(next.companyIds || []);
        }));
      }
      await Promise.all(tasks);
      // Normalise local fields to the trimmed values we persisted so `dirty`
      // settles to false (otherwise a trailing space would keep it "Editing…").
      setName(name.trim());
      setOffice(office.trim());
      setBio(bio.trim());
      setLinkedin(linkedin.trim());
      setPhotoUrl(photoUrl.trim());
      onSaved?.();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const { status } = useAutoSave({ dirty, save, signature: `${name}|${office}|${bio}|${linkedin}|${photoUrl}|${(companyIds || []).join(',')}` });

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
        <div>
          <h2 style={{fontFamily:'var(--font-display)',fontSize:22,margin:0,fontWeight:400}}>Profile</h2>
          <div style={{fontSize:12,color:'var(--fg-muted)',marginTop:4}}>Joined {joined}</div>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <SaveStatus status={status} error={error} dirty={dirty}/>
        </div>
      </div>

      {error && <div style={{padding:10,marginBottom:14,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13}}>{error}</div>}

      <div className="respo-2col" style={{gap:14}}>
        <div className="field" style={{margin:0}}>
          <label>Full name</label>
          <input className="input" value={name} onChange={e => setName(e.target.value)} maxLength={80}/>
        </div>
        <div className="field" style={{margin:0}}>
          <label>Email</label>
          <input className="input" value={user?.email || ''} disabled style={{opacity:0.7}}/>
          <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:4}}>Unique identifier — cannot be changed</div>
        </div>
        <div className="field" style={{margin:0,gridColumn:'1 / -1'}}>
          <TaxonomyMultiSelect
            label="Organizations"
            hint="Organizations you belong to — your employer, sister offices, partner firms. Admins manage the catalog on the Organizations page."
            items={orgCatalog}
            selectedIds={companyIds || []}
            onChange={ids => setCompanyIds(ids)}
            emptyMessage="No organizations in the firm catalog yet. Ask an admin to add one on the Organizations page."
          />
        </div>
        <div className="field" style={{margin:0}}>
          <label>Office</label>
          <select className="select" value={office} onChange={e => setOffice(e.target.value)}>
            <option value="">— pick an office —</option>
            {offices.map(o => <option key={o._id || o.id} value={o.name}>{o.name}</option>)}
            {office && !offices.some(o => o.name === office) && <option value={office}>{office}</option>}
          </select>
        </div>
        <div className="field" style={{margin:0}}>
          <label>LinkedIn URL</label>
          <input className="input" value={linkedin} placeholder="https://www.linkedin.com/in/…" onChange={e => setLinkedin(e.target.value)} maxLength={256}/>
        </div>
        <div className="field" style={{gridColumn:'1 / -1',margin:0}}>
          <label>Profile photo</label>
          <PhotoUploader
            currentUrl={photoUrl}
            onUploaded={url => { setPhotoUrl(url); onSaved?.(); }}
            onClear={() => setPhotoUrl('')}
          />
        </div>
        <div className="field" style={{gridColumn:'1 / -1',margin:0}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
            <label style={{margin:0}}>About</label>
            <button type="button" className="btn-ghost" style={{padding:'4px 12px',fontSize:12}} onClick={() => setIntroOpen(true)}>
              ✨ Auto-generate
            </button>
          </div>
          <textarea className="textarea" rows={6} value={bio} placeholder="Short intro — what you do, what you're known for, what you care about." onChange={e => setBio(e.target.value)} maxLength={2000}/>
          <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:4,textAlign:'right'}}>{bio.length} / 2000</div>
        </div>
      </div>

      <div className="section">
        <div className="section__head"><h2>This week</h2><span className="lede">Usage tracking lands in a later slice — placeholders for now.</span></div>
        <div className="tile-grid">
          <div className="kpi" title="No usage metering yet."><div className="kpi__label">Tokens used</div><div className="kpi__value">—</div><div className="kpi__sub">tracking not implemented</div></div>
          <div className="kpi" title="No skill telemetry yet."><div className="kpi__label">Top skill</div><div className="kpi__value" style={{fontSize:24,marginTop:14}}>—</div><div className="kpi__sub">tracking not implemented</div></div>
          <div className="kpi" title="Memory learning loop not implemented."><div className="kpi__label">Memory items added</div><div className="kpi__value">—</div><div className="kpi__sub">tracking not implemented</div></div>
        </div>
      </div>

      <ComplianceSection/>

      {introOpen && (
        <IntroGenerateModal
          currentBio={bio}
          onAccept={text => { setBio(text); setIntroOpen(false); }}
          onClose={() => setIntroOpen(false)}
        />
      )}
    </div>
  );
};

// Days of daily buckets the backend returns in `series` (mirror of
// SERIES_DAYS in functions/_lib/usage.ts).
const TOKEN_SERIES_DAYS = 30;

const emptyUsage = () => ({ inputTokens: 0, outputTokens: 0, cachedReadTokens: 0, cachedCreationTokens: 0, totalTokens: 0, calls: 0 });
const fmtCalls = n => `${n || 0} call${n === 1 ? '' : 's'}`;

// Daily token throughput as a thin bar chart — one bar per UTC calendar
// day over the trailing TOKEN_SERIES_DAYS window. Missing days render as
// zero so the cadence reads honestly. Matches the flat ComplianceGraph
// aesthetic (responsive viewBox, hairline gridlines, orange marks).
const TokenUsageChart = ({ series }) => {
  const W = 720, H = 150, padL = 36, padR = 8, padT = 12, padB = 22;
  const innerW = W - padL - padR, innerH = H - padT - padB;
  const dayMs = 86_400_000;
  const startOfTodayUtc = Math.floor(Date.now() / dayMs) * dayMs;
  const dayKey = ms => new Date(ms).toISOString().slice(0, 10);
  // Dense axis oldest → newest, ending today (UTC) — keys match the
  // backend's date(created_at/1000,'unixepoch') buckets.
  const days = Array.from({ length: TOKEN_SERIES_DAYS }, (_, i) =>
    dayKey(startOfTodayUtc - (TOKEN_SERIES_DAYS - 1 - i) * dayMs));
  const byDay = new Map((series || []).map(s => [s.day, s]));
  const points = days.map(d => byDay.get(d) || { day: d, inputTokens: 0, outputTokens: 0, totalTokens: 0 });
  const max = Math.max(1, ...points.map(p => p.totalTokens));

  const slot = innerW / TOKEN_SERIES_DAYS;
  const barW = Math.max(2, slot * 0.62);
  const baseY = padT + innerH;
  const xFor = i => padL + i * slot + (slot - barW) / 2;
  const yFor = v => padT + (1 - v / max) * innerH;
  const fmtDay = d => new Date(d + 'T00:00:00Z').toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

  // X labels roughly weekly, always including the most recent day.
  const labelIdx = [];
  for (let i = TOKEN_SERIES_DAYS - 1; i >= 0; i -= 7) labelIdx.unshift(i);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',height:H,display:'block'}} preserveAspectRatio="none">
      {[{ v: 0, dash: undefined }, { v: max, dash: '1 4' }].map((g, gi) => (
        <g key={gi}>
          <line x1={padL} x2={W - padR} y1={yFor(g.v)} y2={yFor(g.v)} stroke="var(--border)" strokeDasharray={g.dash}/>
          <text x={padL - 5} y={yFor(g.v) + 3} fontSize="9" textAnchor="end" fill="var(--fg-muted)">{gi === 0 ? '0' : fmtTokens(max)}</text>
        </g>
      ))}
      {points.map((p, i) => {
        const h = p.totalTokens > 0 ? Math.max(1, baseY - yFor(p.totalTokens)) : 0;
        if (!h) return null;
        return (
          <rect key={i} x={xFor(i)} y={baseY - h} width={barW} height={h} fill="var(--orange)" rx={1}>
            <title>{`${fmtDay(p.day)} · ${fmtTokens(p.totalTokens)} tokens\n${fmtTokens(p.inputTokens)} in / ${fmtTokens(p.outputTokens)} out`}</title>
          </rect>
        );
      })}
      {labelIdx.map(i => (
        <text key={i} x={xFor(i) + barW / 2} y={H - 6} fontSize="9" textAnchor="middle" fill="var(--fg-muted)">{fmtDay(points[i].day)}</text>
      ))}
    </svg>
  );
};

// Token-usage insights: a daily generation chart on top, with total /
// last-7-days / last-24h KPI tiles beneath. `self` (default) reads the
// signed-in user via /api/me/usage; an admin viewing someone else passes
// self={false} + userId, routing through /api/admin/users/:id/usage.
export const TokenInsights = ({ userId, self = true }) => {
  const [data, setData] = React.useState(undefined);  // undefined = loading
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    setData(undefined); setError(null);
    (self ? getMyUsage() : getUserUsage(userId))
      .then(setData)
      .catch(err => { setError(err.message); setData(null); });
  }, [self, userId]);

  if (error) return <div style={{padding:10,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13}}>{error}</div>;
  if (data === undefined) return <div style={{padding:24,color:'var(--fg-muted)',fontSize:13}}>Loading…</div>;

  const total = data.total || emptyUsage();
  const last7d = data.last7d || emptyUsage();
  const last24h = data.last24h || emptyUsage();
  const series = data.series || [];
  const hasData = total.totalTokens > 0;

  return (
    <div>
      <div style={{fontSize:12.5,color:'var(--fg-muted)',marginBottom:8}}>
        Tokens generated per day · last {TOKEN_SERIES_DAYS} days
      </div>
      <TokenUsageChart series={series}/>
      {!hasData && (
        <div style={{fontSize:12.5,color:'var(--fg-muted)',fontStyle:'italic',marginTop:6}}>
          No token usage recorded yet — this fills in as chats and agent runs accumulate.
        </div>
      )}
      <div className="tile-grid" style={{marginTop:16,gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))'}}>
        <div className="kpi">
          <div className="kpi__label">Total tokens used</div>
          <div className="kpi__value">{fmtTokens(total.totalTokens)}</div>
          <div className="kpi__sub">{fmtCalls(total.calls)} · {fmtTokens(total.inputTokens)} in / {fmtTokens(total.outputTokens)} out</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Tokens · last 7 days</div>
          <div className="kpi__value">{fmtTokens(last7d.totalTokens)}</div>
          <div className="kpi__sub">{fmtCalls(last7d.calls)}</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Tokens · last 24 h</div>
          <div className="kpi__value">{fmtTokens(last24h.totalTokens)}</div>
          <div className="kpi__sub">{fmtCalls(last24h.calls)}</div>
        </div>
      </div>
    </div>
  );
};

// `self` (default) loads the signed-in user's own series; an admin
// viewing someone else passes self={false} + that person's userId,
// which routes through the admin compliance endpoint.
export const ComplianceSection = ({ userId, self = true }) => {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    setData(null); setError(null);
    (self ? getMyCompliance() : getUserCompliance(userId))
      .then(setData)
      .catch(err => setError(err.message));
  }, [self, userId]);

  return (
    <div className="section">
      <div className="section__head">
        <h2>Role compliance · last 12 months</h2>
        <span className="lede">Daily score (0–100) from {self ? 'your' : 'their'} reflection agent — how well {self ? 'your' : 'their'} activity aligns with each role's MD file.</span>
      </div>
      {error && <div style={{padding:10,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13}}>{error}</div>}
      {!data && !error && <div style={{padding:24,color:'var(--fg-muted)',fontSize:13}}>Loading…</div>}
      {data && data.functions.length === 0 && (
        <div style={{padding:'10px 0',fontSize:13,color:'var(--fg-muted)'}}>
          {data.note || "No compliance points yet. The reflection agent writes one per function-membership per day — first one lands within 24 h of joining a function."}
        </div>
      )}
      {data && data.functions.length > 0 && (
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          {data.functions.map(f => <ComplianceGraph key={f.functionId} fn={f}/>)}
        </div>
      )}
    </div>
  );
};

const ComplianceGraph = ({ fn }) => {
  const W = 720, H = 110, padL = 28, padR = 6, padT = 8, padB = 18;
  const innerW = W - padL - padR, innerH = H - padT - padB;
  // X axis: 365-day window ending today; one column per day so gaps show.
  const todayMs = Date.now();
  const days = Array.from({ length: 365 }, (_, i) => {
    const d = new Date(todayMs - (364 - i) * 86_400_000);
    return d.toISOString().slice(0, 10);
  });
  const pointsByDay = new Map(fn.points.map(p => [p.day, p]));
  const xFor = idx => padL + (idx / 364) * innerW;
  const yFor = score => padT + (1 - score / 100) * innerH;

  // Build a polyline over only the days that have data; missing days
  // create breaks so the graph is honest about sparseness.
  const segments = [];
  let current = [];
  days.forEach((d, i) => {
    const p = pointsByDay.get(d);
    if (p) current.push({ x: xFor(i), y: yFor(p.score), score: p.score, day: d, reasoning: p.reasoning });
    else if (current.length) { segments.push(current); current = []; }
  });
  if (current.length) segments.push(current);

  const colorFor = score =>
    score >= 75 ? 'var(--support-green)' :
    score >= 50 ? 'var(--orange)'        :
                  'var(--support-red)';

  const fmtMonth = (idx) => {
    const d = new Date(todayMs - (364 - idx) * 86_400_000);
    return d.toLocaleDateString(undefined, { month: 'short' });
  };
  // Vertical month gridlines: pick day-1 indices.
  const monthTicks = [];
  for (let i = 0; i < 365; i++) {
    const d = new Date(todayMs - (364 - i) * 86_400_000);
    if (d.getDate() === 1) monthTicks.push(i);
  }

  return (
    <div style={{padding:'12px 0',borderBottom:'1px solid var(--border)'}}>
      <div style={{display:'flex',alignItems:'baseline',gap:10,marginBottom:8,flexWrap:'wrap'}}>
        <h3 style={{margin:0,fontSize:15}}>{fn.functionName}</h3>
        <span style={{fontSize:11,color:'var(--fg-muted)',fontFamily:'JetBrains Mono'}}>/roles/{fn.functionSlug}.md</span>
        <div style={{flex:1}}/>
        <span style={{fontSize:12,color:'var(--fg-muted)'}}>
          {fn.current === null
            ? 'no points'
            : <>current: <strong style={{color:colorFor(fn.current)}}>{fn.current}</strong></>}
          {fn.average365 !== null && <> · 12-mo avg: <strong style={{color:colorFor(fn.average365)}}>{fn.average365}</strong></>}
          <> · {fn.points.length} day{fn.points.length === 1 ? '' : 's'}</>
        </span>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',height:H,display:'block'}} preserveAspectRatio="none">
        {/* gridlines: 0/50/100 */}
        {[0, 50, 100].map(g => (
          <g key={g}>
            <line x1={padL} x2={W - padR} y1={yFor(g)} y2={yFor(g)} stroke="var(--border)" strokeDasharray={g === 50 ? '2 3' : '1 4'}/>
            <text x={padL - 4} y={yFor(g) + 3} fontSize="9" textAnchor="end" fill="var(--fg-muted)">{g}</text>
          </g>
        ))}
        {/* month ticks */}
        {monthTicks.map(i => (
          <g key={i}>
            <line x1={xFor(i)} x2={xFor(i)} y1={padT} y2={H - padB} stroke="var(--border)" strokeWidth={0.5}/>
            <text x={xFor(i)} y={H - 4} fontSize="9" textAnchor="middle" fill="var(--fg-muted)">{fmtMonth(i)}</text>
          </g>
        ))}
        {/* the score path(s) */}
        {segments.map((seg, si) => (
          <g key={si}>
            <polyline
              points={seg.map(p => `${p.x},${p.y}`).join(' ')}
              fill="none"
              stroke="var(--orange)"
              strokeWidth={1.5}
            />
            {seg.map((p, pi) => (
              <circle key={pi} cx={p.x} cy={p.y} r={2} fill={colorFor(p.score)}>
                <title>{`${p.day} · ${p.score}/100${p.reasoning ? '\n' + p.reasoning : ''}`}</title>
              </circle>
            ))}
          </g>
        ))}
      </svg>
    </div>
  );
};

const IntroGenerateModal = ({ currentBio, onAccept, onClose }) => {
  const [extra, setExtra] = React.useState('');
  const [tone, setTone] = React.useState('crisp');
  const [busy, setBusy] = React.useState(false);
  const [draft, setDraft] = React.useState(null);
  const [error, setError] = React.useState(null);

  const generate = async () => {
    setBusy(true); setError(null);
    try {
      const { draft } = await generateMyIntro({ extra: extra.trim() || undefined, tone });
      setDraft(draft);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-mask" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{position:'relative',maxWidth:680,width:'95%'}}>
        <button className="modal__close" onClick={onClose}>×</button>
        <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--orange)',marginBottom:6,fontWeight:600}}>Auto-generate</div>
        <h3 style={{marginBottom:8}}>Draft your intro with Claude.</h3>
        <p style={{color:'var(--fg-muted)',fontSize:13.5,marginBottom:16}}>
          Writes a short professional introduction about you in the third person, highlighting your skills, services, industries, and client experience. Uses your name, title, LinkedIn URL, taxonomy memberships, and current bio as inputs. Paste extra context below if you want — your LinkedIn About text, specific focus areas, or anything else that should shape the draft.
        </p>

        <div className="field">
          <label>Tone</label>
          <div style={{display:'flex',gap:6}}>
            {[
              { id: 'crisp',  label: 'Crisp' },
              { id: 'warm',   label: 'Warm' },
              { id: 'punchy', label: 'Punchy' },
            ].map(t => (
              <span key={t.id} className={`tag ${tone === t.id ? 'tag--orange' : ''}`} style={{cursor:'pointer'}} onClick={() => setTone(t.id)}>{t.label}</span>
            ))}
          </div>
        </div>

        <div className="field">
          <label>Extra context (optional)</label>
          <textarea
            className="textarea"
            rows={5}
            placeholder="Paste your LinkedIn About text, focus areas, recent work, anything you want emphasised…"
            value={extra}
            onChange={e => setExtra(e.target.value)}
            maxLength={4000}
          />
          <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:4,textAlign:'right'}}>{extra.length} / 4000</div>
        </div>

        {currentBio && !draft && (
          <div style={{padding:'8px 0',fontSize:12,color:'var(--fg-muted)',marginBottom:14}}>
            Your existing bio will be passed in too — Claude will refine it rather than discard.
          </div>
        )}

        {draft && (
          <div className="field">
            <label>Draft</label>
            <textarea
              className="textarea"
              rows={9}
              value={draft}
              onChange={e => setDraft(e.target.value)}
              style={{fontFamily:'inherit'}}
            />
            <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:4}}>
              Tweak it here if you want — or regenerate with different inputs.
            </div>
          </div>
        )}

        {error && <div style={{padding:10,marginBottom:14,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13}}>{error}</div>}

        <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:12}}>
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          {draft && (
            <button type="button" className="btn-ghost" onClick={generate} disabled={busy}>
              {busy ? 'Generating…' : 'Regenerate'}
            </button>
          )}
          {!draft ? (
            <button type="button" className="btn-orange" disabled={busy} onClick={generate}>
              {busy ? 'Generating…' : 'Generate'}
            </button>
          ) : (
            <button type="button" className="btn-orange" disabled={busy} onClick={() => onAccept(draft)}>
              Use this
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const PhotoUploader = ({ currentUrl, onUploaded, onClear }) => {
  const inputRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  const pickFile = () => inputRef.current?.click();

  const onChange = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null); setBusy(true);
    try {
      const { photoUrl } = await uploadMyPhoto(file);
      onUploaded(photoUrl);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';  // allow re-picking the same file
    }
  };

  return (
    <div style={{display:'flex',alignItems:'center',gap:14}}>
      {currentUrl ? (
        <img
          src={currentUrl}
          alt="profile"
          style={{width:64,height:64,borderRadius:'50%',objectFit:'cover',background:'var(--bg-card)'}}
          onError={e => { e.currentTarget.style.display = 'none'; }}
        />
      ) : (
        <div style={{width:64,height:64,borderRadius:'50%',background:'var(--bg-card)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,color:'var(--fg-muted)'}}>
          no photo
        </div>
      )}
      <div style={{display:'flex',flexDirection:'column',gap:6,flex:1}}>
        <div style={{display:'flex',gap:8}}>
          <button type="button" className="btn-ghost" onClick={pickFile} disabled={busy} style={{padding:'5px 12px',fontSize:12.5}}>
            {busy ? 'Uploading…' : (currentUrl ? 'Replace' : 'Upload')}
          </button>
          {currentUrl && (
            <button type="button" className="btn-ghost" onClick={onClear} disabled={busy} style={{padding:'5px 12px',fontSize:12.5,color:'var(--support-red)'}}>
              Remove
            </button>
          )}
        </div>
        <div style={{fontSize:11,color:'var(--fg-muted)'}}>
          PNG / JPG / WebP / GIF · max 2 MB · stored in your Cloudflare R2 bucket
        </div>
        {error && <div style={{fontSize:11.5,color:'#A20235'}}>{error}</div>}
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          style={{display:'none'}}
          onChange={onChange}
        />
      </div>
    </div>
  );
};

const taxoSig = p => p ? ['serviceIds','industryIds','clientIds','projectIds','companyIds']
  .map(k => [...(p[k] || [])].sort().join(',')).join('|') : '';

// Taxonomy editor. `self` (default) loads + saves the signed-in user's
// own memberships; when an admin edits someone else, pass self={false}
// + the target userId + initialPicks (the target's current memberships)
// — writes then route through the admin endpoint. `showStaffing` renders
// the inline staffing list (the self profile uses it; the unified people
// page suppresses it because it has its own Staffing section).
export const ProfileExperience = ({ userId, self = true, initialPicks = null, showStaffing = true }) => {
  const [catalog, setCatalog] = React.useState(null);
  const [picks, setPicks] = React.useState(null);
  const [original, setOriginal] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    Promise.all([
      listTaxonomies(),
      self ? getMyTaxonomies() : Promise.resolve(initialPicks || {}),
      listAllProjects().catch(() => []),
    ])
      .then(([cat, mine, projects]) => {
        setCatalog({ ...cat, projects, companies: cat.companies || [] });
        const next = {
          serviceIds: mine.serviceIds || [],
          industryIds: mine.industryIds || [],
          clientIds: mine.clientIds || [],
          projectIds: mine.projectIds || [],
          companyIds: mine.companyIds || [],
        };
        setPicks(next);
        setOriginal(next);
      })
      .catch(err => setError(err.message));
    // initialPicks is read once at mount for the admin path.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [self, userId]);

  const dirty = !!picks && taxoSig(picks) !== taxoSig(original);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      const next = self ? await putMyTaxonomies(picks) : await putAdminUserTaxonomies(userId, picks);
      setPicks({
        serviceIds: next.serviceIds || [],
        industryIds: next.industryIds || [],
        clientIds: next.clientIds || [],
        projectIds: next.projectIds || [],
        companyIds: next.companyIds || [],
      });
      setOriginal({
        serviceIds: next.serviceIds || [],
        industryIds: next.industryIds || [],
        clientIds: next.clientIds || [],
        projectIds: next.projectIds || [],
        companyIds: next.companyIds || [],
      });
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const { status } = useAutoSave({ dirty, save, signature: taxoSig(picks), enabled: !!picks });

  // Decorate each project label with its client name so duplicate project
  // names disambiguate. Memoised on the catalog and resolved through a Map
  // so it's O(projects + clients) once per load — the previous inline
  // `.map`/`.find` was O(projects × clients) and re-ran on every render
  // (every keystroke and every autosave), which made the picker laggy.
  const projectItems = React.useMemo(() => {
    const clientName = new Map((catalog?.clients || []).map(c => [c._id, c.name]));
    return (catalog?.projects || []).map(p => ({
      ...p,
      name: `${p.name} · ${clientName.get(p.clientId) || '—'}`,
    }));
  }, [catalog]);

  if (error && !picks) {
    return <div style={{padding:12,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13}}>{error}</div>;
  }
  if (!catalog || !picks) {
    return <div style={{padding:24,color:'var(--fg-muted)',fontSize:13}}>Loading…</div>;
  }

  return (
    <div style={{display:'grid',gap:22,maxWidth:820}}>
      <div style={{display:'flex',justifyContent:'flex-end',gap:10,alignItems:'center'}}>
        <SaveStatus status={status} error={error} dirty={dirty}/>
      </div>

      <TaxonomyMultiSelect
        label="Core services"
        hint="What your firm sells and you personally deliver. Start typing to search."
        items={catalog.services}
        selectedIds={picks.serviceIds}
        onChange={ids => setPicks(p => ({ ...p, serviceIds: ids }))}
        emptyMessage="No services in the firm catalog yet. Ask an admin to add one on the Services page."
      />
      <TaxonomyMultiSelect
        label="Core industries"
        hint="Markets you work in."
        items={catalog.industries}
        selectedIds={picks.industryIds}
        onChange={ids => setPicks(p => ({ ...p, industryIds: ids }))}
        emptyMessage="No industries in the firm catalog yet. Ask an admin to add one on the Industries page."
      />
      <TaxonomyMultiSelect
        label="Clients"
        hint="Engagements you're staffed on."
        items={catalog.clients}
        selectedIds={picks.clientIds}
        onChange={ids => setPicks(p => ({ ...p, clientIds: ids }))}
        emptyMessage="No clients in the firm catalog yet. Ask an admin to add one on the Clients page."
      />
      <TaxonomyMultiSelect
        label="Projects"
        hint="Specific engagements you're on. Projects belong to a client."
        items={projectItems}
        selectedIds={picks.projectIds || []}
        onChange={ids => setPicks(p => ({ ...p, projectIds: ids }))}
        emptyMessage="No projects yet. Ask an admin to add one on the Projects page."
      />
      <TaxonomyMultiSelect
        label="Organizations"
        hint="Organizations you belong to — your employer + sister offices + partner firms you're staffed across."
        items={catalog.companies || []}
        selectedIds={picks.companyIds || []}
        onChange={ids => setPicks(p => ({ ...p, companyIds: ids }))}
        emptyMessage="No organizations in the firm catalog yet. Ask an admin to add one on the Organizations page."
      />

      {showStaffing && userId && <StaffingSection userId={userId} self={self} viewerIsAdmin={!self} title="Project history"/>}
    </div>
  );
};

const ProfileActivity = () => {
  const [entries, setEntries] = React.useState(null);
  const [error, setError] = React.useState(null);

  const reload = React.useCallback(() => {
    setError(null);
    myActivity(200).then(setEntries).catch(err => setError(err.message));
  }, []);

  React.useEffect(() => { reload(); }, [reload]);

  if (error) return <div style={{padding:12,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13}}>{error}</div>;
  if (entries === null) return <div style={{padding:24,color:'var(--fg-muted)',fontSize:13}}>Loading…</div>;

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12}}>
        <h2 style={{margin:0,fontSize:18}}>Your activity</h2>
        <span style={{fontSize:12,color:'var(--fg-muted)'}}>· {entries.length} entries</span>
        <div style={{flex:1}}/>
        <button className="btn-ghost" onClick={reload} style={{fontSize:12,padding:'4px 10px'}}>Refresh</button>
      </div>

      <div style={{fontSize:12.5,color:'var(--fg-muted)',marginBottom:14,lineHeight:1.5}}>
        Every meaningful action you take is logged here — invitations issued, workers spawned, OrgRepo edits, profile updates, intents created. Each row also feeds the daily learning agents for the functions you're in.
      </div>

      {entries.length === 0 ? (
        <div style={{padding:'10px 0',fontSize:13,color:'var(--fg-muted)'}}>
          No activity yet. Try logging out and back in, or spawning a worker — it'll show up here.
        </div>
      ) : (
        <table className="tbl">
          <thead><tr><th style={{width:140}}>When</th><th style={{width:200}}>Action</th><th>Target</th><th style={{width:90}}>Severity</th></tr></thead>
          <tbody>
            {entries.map(e => (
              <tr key={e.id}>
                <td style={{color:'var(--fg-muted)',fontSize:12}}>{relTime(e.createdAt)}</td>
                <td style={{fontFamily:'JetBrains Mono',fontSize:12}}>{e.action}</td>
                <td>{e.target ? <span><span style={{color:'var(--fg-muted)',fontSize:11}}>{e.target.type}: </span>{e.target.label || e.target.id || '—'}</span> : '—'}</td>
                <td><span style={{fontSize:11,padding:'2px 8px',borderRadius:4,background:e.severity==='high'?'#FCE6E0':e.severity==='medium'?'#FFE7DC':'var(--bg-card)',color:e.severity==='high'?'#A20235':e.severity==='medium'?'#B83C0F':'var(--fg-muted)',textTransform:'uppercase',letterSpacing:'0.04em'}}>{e.severity}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

function fmtTokens(n) {
  if (!Number.isFinite(n)) return '—';
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}k`;
  return `${(n / 1_000_000).toFixed(n < 10_000_000 ? 2 : 1)}M`;
}

function relTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  const abs = Math.abs(Date.now() - d.getTime());
  const min = 60_000, hr = 60 * min, day = 24 * hr;
  if (abs < min)     return 'just now';
  if (abs < hr)      return `${Math.round(abs / min)}m ago`;
  if (abs < day)     return `${Math.round(abs / hr)}h ago`;
  if (abs < 7 * day) return `${Math.round(abs / day)}d ago`;
  return d.toLocaleDateString();
}

// `self` (default) wires the functional controls (Profile Advisor) to
// the signed-in user; an admin viewing someone else passes self={false}
// + userId so those controls read/write the target user. The remaining
// rows are not-yet-wired placeholders and render the same for everyone.
export const ProfileSettings = ({ user, userId, self = true }) => (
  <div style={{display:'grid',gap:18,maxWidth:680}}>
    <div className="section__head" style={{margin:0}}><h2>Settings</h2></div>

    <SettingsRow label="Public agents">
      <PublicAgentsSettings userId={userId} self={self}/>
    </SettingsRow>

    <SettingsRow label="Notifications">
      <div style={{display:'flex',flexDirection:'column',gap:6,fontSize:13.5}}>
        <label style={{display:'flex',alignItems:'center',gap:8}}><input type="checkbox" defaultChecked/> Worker completed</label>
        <label style={{display:'flex',alignItems:'center',gap:8}}><input type="checkbox" defaultChecked/> Strategic commit needs your review</label>
        <label style={{display:'flex',alignItems:'center',gap:8}}><input type="checkbox"/> News digest, daily 8:00</label>
        <label style={{display:'flex',alignItems:'center',gap:8}}><input type="checkbox" defaultChecked/> Trust Engineer alerts (drift, anomalies)</label>
      </div>
    </SettingsRow>

    {/* Connectors are intentionally disabled until the integration layer ships.
        They're rendered greyed-out + non-interactive so users see what's
        coming but don't try to click. */}
    <SettingsRow label="Integrations">
      <div style={{fontSize:12,color:'var(--fg-muted)',marginBottom:10,fontStyle:'italic'}}>
        Connectors are disabled — the integration layer ships in a later slice.
      </div>
      <div style={{display:'grid',gap:8,opacity:0.45,pointerEvents:'none',userSelect:'none'}}>
        {[
          {n:'Microsoft 365', s: user?.email ? `Would link ${user.email}` : 'Not connected'},
          {n:'Outlook (Email drafter)', s:'Not connected'},
          {n:'Figma', s:'Not connected'},
          {n:'Confluence', s:'Not connected'},
          {n:'CRM (Salesforce)', s:'Not connected'},
        ].map((it,i) => (
          <div key={i} aria-disabled style={{display:'flex',alignItems:'center',padding:'10px 0',borderBottom:'1px solid var(--border)',gap:12}}>
            <div style={{flex:1}}>
              <div style={{fontWeight:500,fontSize:14}}>{it.n}</div>
              <div style={{fontSize:11.5,color:'var(--fg-muted)'}}>{it.s}</div>
            </div>
            <button className="btn-ghost" style={{padding:'5px 12px',fontSize:12}} tabIndex={-1} disabled>Connect</button>
          </div>
        ))}
      </div>
    </SettingsRow>

    <SettingsRow label="Data & memory">
      <div style={{display:'flex',flexDirection:'column',gap:6,fontSize:13.5}}>
        <label style={{display:'flex',alignItems:'center',gap:8}}><input type="checkbox" defaultChecked/> Store chat history</label>
        <label style={{display:'flex',alignItems:'center',gap:8}}><input type="checkbox" defaultChecked/> Allow function MD learning from my work</label>
        <label style={{display:'flex',alignItems:'center',gap:8}}><input type="checkbox"/> Build a digital twin (advanced)</label>
        <label style={{display:'flex',alignItems:'center',gap:8}}><input type="checkbox"/> Send anonymised patterns to model provider</label>
      </div>
      <div style={{marginTop:10,display:'flex',gap:8}}>
        <button className="btn-ghost" style={{padding:'6px 12px',fontSize:12.5}}>Export my data</button>
        <button className="btn-ghost" style={{padding:'6px 12px',fontSize:12.5,color:'var(--support-red)'}}>Wipe memory</button>
      </div>
    </SettingsRow>
  </div>
);

const SettingsRow = ({ label, children }) => (
  <div style={{padding:'16px 0',borderBottom:'1px solid var(--border)'}}>
    <div style={{fontFamily:'var(--font-display)',fontSize:17,marginBottom:12,fontWeight:400}}>{label}</div>
    {children}
  </div>
);

// Per-user controls for firm-wide ("public") agents that act on the
// user. Today that's the Profile Advisor, which messages them daily at
// 12:00 about empty profile fields. Switching it off here means the
// daily nudge skips them.
const PublicAgentsSettings = ({ userId, self = true }) => {
  const [state, setState] = React.useState(null);  // { enabled, completeness }
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    (self ? getMyProfileAdvisor() : getUserProfileAdvisor(userId))
      .then(setState)
      .catch(() => setState({ enabled: true, completeness: null }));
  }, [self, userId]);

  const toggle = () => {
    if (!state) return;
    const next = !state.enabled;
    setBusy(true);
    (self ? setMyProfileAdvisor(next) : setUserProfileAdvisor(userId, next))
      .then(() => setState(s => ({ ...s, enabled: next })))
      .catch(() => {})
      .finally(() => setBusy(false));
  };

  const pct = state?.completeness?.completenessPct;
  const missing = state?.completeness?.missing || [];

  return (
    <div>
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:12}}>
        <div style={{flex:1}}>
          <div style={{fontWeight:500,fontSize:14}}>Profile Advisor</div>
          <div style={{fontSize:12.5,color:'var(--fg-muted)',marginTop:3,lineHeight:1.5}}>
            {self
              ? "Checks your profile once a day at 12:00 and opens a chat asking about anything that's still empty — you answer right there and it fills it in for you. Turn it off and it'll leave you alone."
              : "Checks this person's profile once a day at 12:00 and opens a chat asking about anything that's still empty — they answer right there and it fills it in for them. Turn it off and it'll leave them alone."}
          </div>
          {state?.enabled && pct != null && (
            <div style={{fontSize:12,color:'var(--fg-muted)',marginTop:8}}>
              {self ? 'Your' : 'Their'} profile is <strong style={{color:'var(--fg)'}}>{pct}% complete</strong>
              {missing.length > 0 && <> · still empty: {missing.join(', ')}</>}
            </div>
          )}
        </div>
        <button
          onClick={toggle}
          disabled={busy || !state}
          style={{flexShrink:0,padding:'6px 14px',fontSize:12.5,fontWeight:600,borderRadius:8,cursor:(busy||!state)?'default':'pointer',
                  border:'1px solid var(--border)',
                  background: state?.enabled ? 'var(--bg)' : 'var(--orange)',
                  color: state?.enabled ? 'var(--fg)' : '#fff'}}
        >
          {!state ? '…' : busy ? '…' : state.enabled ? 'Turn off' : 'Turn on'}
        </button>
      </div>
    </div>
  );
};
